/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {createContext, useContext} from "react"
import {useSelector} from 'react-redux';
import {useAppSelector} from "../../hooks";
import {RootState} from "../../store";
import {IMessage} from "../../../containers/pages/radar/SensorRadar/FileProcessingMessages";
import {IInstructionsFormData} from "../../../containers/components/Wizard/types";

export const SliceContext = createContext<any>({})

const SliceProvider = ({slice, children}: { slice: any, children: React.ReactNode }) => (
  <SliceContext.Provider value={slice}>{children}</SliceContext.Provider>
)

const useSliceActions = () => useContext(SliceContext).actions

const useSliceSelector = () => {
  const {name} = useContext(SliceContext)
  return useSelector((state: any) => {
    return state['wizard'][name ?? '']
  })
}

const useGetWizardState = (state: RootState) => {
  const {name} = useContext(SliceContext)
  return state.wizard[name]
}

const useSelectWizardOpenc = () => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState) => {
    return state.wizard[name].isWizardOpen
  })
}

const useSelectFileUploadMessages = () => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState) => {
    return state.wizard[name].fileUploadMessages
  })
}

const useSelectFileUploadResponse = () => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState) => {
    return state.wizard[name].fileUploadResponse
  })
}

const useSelectFileUploadHasErrors = () => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState) => {
    return state.wizard[name].fileUploadHasErrors
  })
}

const useSelectErrorMessageList = (index: number) => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): IMessage[] => {
    return state.wizard[name].allErrorMessages[index] ?? []
  })
}

const useSelectWarningMessageList = (index: number) => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): IMessage[] => {
    return state.wizard[name].allWarningMessages[index] ?? []
  })
}

const useSelectAllErrorMessages = () => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): IMessage[][] => {
    return state.wizard[name].allErrorMessages
  })
}

const useSelectAllWarningMessages = () => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): IMessage[][] => {
    return state.wizard[name].allWarningMessages
  })
}

const useSelectRefreshTableCount = () => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): number => {
    return state.wizard[name].refreshTableCount
  })
}

const useSelectInstructionsFormData = (): IInstructionsFormData | null => {
  const response: any = useSelectFileUploadResponse()
  if (response == null) return null
  const instructions_form_data: IInstructionsFormData | null = response?.['instructions_form_data'] ?? null
  return instructions_form_data
}

const useSelectCosmosData = (): any => {
  const response: any = useSelectFileUploadResponse()
  if (response == null) return null
  return response?.cosmos_data
}

const useSelectWizardMetaData = (): any => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): IMessage[][] => {
    return state.wizard[name].metaData
  })
}

const useSelectFileDataHasErrors = (): boolean => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): boolean => {
    return state.wizard[name].fileDataHasErrors
  })
}

const useSelectFileDataHasWarnings = (): boolean => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): boolean => {
    return state.wizard[name].fileDataHasWarnings
  })
}

const useSelectDontAllowSubmit = (): string[] => {
  const {name} = useContext(SliceContext)
  return useAppSelector((state: RootState): string[] => {
    return state.wizard[name].dontAllowSubmit
  })
}

export {
  useSelectWizardOpenc,
  useSelectFileUploadMessages,
  useSelectFileUploadResponse,
  useSelectFileUploadHasErrors,
  useSelectErrorMessageList,
  useSelectWarningMessageList,
  useSelectAllErrorMessages,
  useSelectFileDataHasErrors,
  useSelectFileDataHasWarnings,
  useSelectDontAllowSubmit,
  useSelectInstructionsFormData,
  useSelectRefreshTableCount,
  useSelectCosmosData,
  useSelectWizardMetaData,
  useSelectAllWarningMessages
}
export default SliceProvider
export {useSliceActions, useSliceSelector}